import { Injectable } from "@angular/core";
import { EmergencyContact, EmergencyContactHelper } from "@app/project/model/emergency-contact";
import { EmergencyContactDbService } from "@app/project/services/emergency-contact-db.service";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { ClareDbCollection, wrapBulkResult, wrapResult } from "@app/shared/resources/resource-objectifier";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { exhaustMap, map, switchMap } from "rxjs/operators";
import { DataActions, EmergencyContactActions } from "../actions";
import { ActionPayload, DeleteAllActionPayload } from "../actions/emergency-contact.actions";

@Injectable()
export class EmergencyContactEffects {
    constructor(
        private actions$: Actions,
        private dbService: EmergencyContactDbService,
        private deleteConfirmationService: DeleteConfirmationService
    ) {}

    wrapResult(this: void, source$: Observable<EmergencyContact>) {
        return wrapResult<EmergencyContact>(source$, "emergencyContact");
    }

    wrapBulkResult(this: void, source$: Observable<ClareDbCollection<EmergencyContact>>) {
        return wrapBulkResult<EmergencyContact>(source$, "emergencyContacts");
    }

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.load),
            switchMap(({ projectId }) =>
                DataActions.toAction2()(
                    this.dbService.getEmergencyContacts(projectId).pipe(map(emergencyContacts => ({ emergencyContacts }))),
                    EmergencyContactActions.loadSuccess,
                    EmergencyContactActions.loadFailure
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.create),
            exhaustMap(({ emergencyContact, callbackEmitter }) =>
                DataActions.toAction2()(
                    this.dbService.createEmergencyContact(emergencyContact).pipe(map(emergencyContact => ({ emergencyContact }))),
                    EmergencyContactActions.createSuccess,
                    EmergencyContactActions.createFailure,
                    callbackEmitter
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.update),
            exhaustMap(({ emergencyContact, callbackEmitter }) =>
                DataActions.toAction2()(
                    this.dbService.updateContact(emergencyContact).pipe(map(() => ({ emergencyContact }))),
                    EmergencyContactActions.updateSuccess,
                    EmergencyContactActions.updateFailure,
                    callbackEmitter
                )
            )
        )
    );

    upsertSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.createSuccess, EmergencyContactActions.updateSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.saved, null, EmergencyContactHelper.dbTypeName))
        )
    );

    upsertFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.createFailure, EmergencyContactActions.updateFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, EmergencyContactHelper.dbTypeName))
        )
    );

    updateBulk$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.bulkUpdate),
            exhaustMap(({ emergencyContacts, callbackEmitter }) =>
                DataActions.toAction2()(
                    this.dbService.bulkUpdate(emergencyContacts).pipe(
                        map(() => {
                            const collection = emergencyContacts.reduce((collection, c) => {
                                collection[c.id] = c;
                                return collection;
                            }, {});

                            return { emergencyContacts: collection };
                        })
                    ),
                    EmergencyContactActions.bulkUpdateSuccess,
                    EmergencyContactActions.bulkUpdateFailure,
                    callbackEmitter
                )
            )
        )
    );

    updateBulkSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.bulkUpdateSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.saved, null, EmergencyContactHelper.dbTypeNamePlural))
        )
    );

    updateBulkFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.bulkUpdateFailure),
            map(DataActions.handleErrorMessage(ActionWord.saved, EmergencyContactHelper.dbTypeNamePlural))
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteItem),
            exhaustMap(({ emergencyContact, callbackEmitter }) =>
                DataActions.toAction2()(
                    this.dbService.deleteContact(emergencyContact).pipe(map(() => ({ emergencyContact }))),
                    EmergencyContactActions.deleteSuccess,
                    EmergencyContactActions.deleteFailure,
                    callbackEmitter
                )
            )
        )
    );

    deleteConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteConfirmation),
            exhaustMap(({ emergencyContact }) =>
                this.deleteConfirmationService.open$<ActionPayload>(
                    { emergencyContact },
                    EmergencyContactActions.deleteDismiss,
                    EmergencyContactActions.deleteItem,
                    `this ${EmergencyContactHelper.dbTypeName}`
                )
            )
        )
    );

    // Delete All Effects
    deleteAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteAll),
            exhaustMap(({ emergencyContacts, callbackEmitter }) =>
                DataActions.toAction2()(
                    this.dbService.bulkDelete({ data: emergencyContacts, logData: { projectId: emergencyContacts[0]?.projectId } }).pipe(
                        map(() =>
                            emergencyContacts.reduce((collection, c) => {
                                collection[c.id] = c;
                                return collection;
                            }, {})
                        ),
                        map(contacts => ({ emergencyContacts: contacts }))
                    ),
                    EmergencyContactActions.deleteAllSuccess,
                    EmergencyContactActions.deleteAllFailure,
                    callbackEmitter
                )
            )
        )
    );

    deleteAllConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteAllConfirmation),
            exhaustMap(({ emergencyContacts, callbackEmitter }) =>
                this.deleteConfirmationService.open$<DeleteAllActionPayload>(
                    { emergencyContacts, callbackEmitter },
                    EmergencyContactActions.deleteAllDismiss,
                    EmergencyContactActions.deleteAll,
                    `all ${EmergencyContactHelper.dbTypeName}s from this project`
                )
            )
        )
    );

    deleteAllSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteAllSuccess),
            map(DataActions.handleSuccessMessage2(ActionWord.removed, null, EmergencyContactHelper.dbTypeNamePlural))
        )
    );

    deleteAllFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EmergencyContactActions.deleteAllFailure),
            map(DataActions.handleErrorMessage(ActionWord.removed, EmergencyContactHelper.dbTypeNamePlural))
        )
    );
}
